import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaTable } from '@react-icons/all-files/fa/FaTable';
import { FaRegCalendarCheck } from '@react-icons/all-files/fa/FaRegCalendarCheck';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { DeleteButton, RenderActions } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { deleteEntity, getEntities, reset } from '../../../../manager/profase-periodo.reducer';
import { formatTipoPeriodoLabel, TIPO_PERIODO_OPTIONS } from '../../../../shared/workflow-periodicidad';
import PeriodoTipoResumenBar, { type PeriodoTipoResumen } from '../../../../shared/periodo-tipo-resumen-bar';

const PERIODO_TABLE_FILTERS = [
  { label: 'ID', name: 'id' },
  {
    label: 'Tipo',
    name: 'tipo',
    type: 'select' as const,
    single: true,
    options: TIPO_PERIODO_OPTIONS,
  },
  { label: 'Año', name: 'anio' },
  { label: 'Número', name: 'numero' },
  {
    label: 'Estado',
    name: 'estado',
    type: 'select' as const,
    single: true,
    options: [
      { value: 'NUEVO', label: 'Nuevo' },
      { value: 'GENERADO', label: 'Generado' },
    ],
  },
];

function filtersFromSearch(
  search: string,
  filterDefs: typeof PERIODO_TABLE_FILTERS,
): { filterName: string; filterValue: string }[] {
  const urlParams = new URLSearchParams(search);
  return filterDefs.flatMap(f => {
    const values = urlParams.getAll(f.name).filter(v => v !== '');
    if (f.single) {
      const v = values.length > 0 ? values[values.length - 1] : null;
      return v != null ? [{ filterName: f.name, filterValue: v }] : [];
    }
    return values.map(v => ({ filterName: f.name, filterValue: v }));
  });
}

function buildTipoResumenParams(search: string): URLSearchParams {
  const params = new URLSearchParams();
  filtersFromSearch(search, PERIODO_TABLE_FILTERS)
    .filter(f => f.filterName !== 'tipo')
    .forEach(f => {
      params.append(f.filterName, f.filterValue);
    });
  return params;
}

function setTipoFilter(
  searchParams: URLSearchParams,
  tipo: string,
): URLSearchParams {
  const params = new URLSearchParams(searchParams);
  params.delete('tipo');
  params.set('tipo', String(tipo).toUpperCase());
  params.set('page', '1');
  return params;
}

const ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  NUEVO: { label: 'Nuevo', bg: '#fef9c3', color: '#854d0e' },
  GENERADO: { label: 'Generado', bg: '#eff6ff', color: '#1d4ed8' },
};

const fmtDate = (v: unknown) => {
  if (v == null || v === '') return '—';
  const s = String(v);
  const [y, m, d] = s.slice(0, 10).split('-');
  if (y && m && d) return `${d}/${m}/${y}`;
  return s;
};

function TipoLabel({ tipo }: { tipo?: string }) {
  if (!tipo) return <span style={{ color: '#94a3b8' }}>—</span>;
  return <span>{formatTipoPeriodoLabel(tipo)}</span>;
}

function EstadoBadge({ estado }: { estado?: string }) {
  if (!estado) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const cfg = ESTADO_CONFIG[estado.toUpperCase()] ?? { label: estado, bg: '#f1f5f9', color: '#475569' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      background: cfg.bg,
      color: cfg.color,
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

export default function TablaPeriodosPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.profasePeriodo.entities);
  const totalItems = useAppSelector(s => s.profasePeriodo.totalItems);
  const loading = useAppSelector(s => s.profasePeriodo.loading);
  const updating = useAppSelector(s => s.profasePeriodo.updating);
  const updateSuccess = useAppSelector(s => s.profasePeriodo.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);
  const [tipoResumen, setTipoResumen] = useState<PeriodoTipoResumen[]>([]);
  const [resumenLoading, setResumenLoading] = useState(false);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const fetchTipoResumen = useCallback(async () => {
    setResumenLoading(true);
    try {
      const params = buildTipoResumenParams(location.search);
      params.append('cacheBuster', String(Date.now()));
      const res = await axios.get<PeriodoTipoResumen[]>(
        `api/profase-periodos/tipo/resumen?${params.toString()}`,
      );
      setTipoResumen(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTipoResumen([]);
    } finally {
      setResumenLoading(false);
    }
  }, [location.search]);

  useEffect(() => {
    fetchTipoResumen();
  }, [fetchTipoResumen]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      fetchTipoResumen();
      showToast('success', 'Periodo eliminado correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const handleTipoFilter = useCallback((tipo: string) => {
    setSearchParams(setTipoFilter(searchParams, tipo));
  }, [searchParams, setSearchParams]);

  const periodoLabel = (row: any) => {
    const tipoNorm = String(row.tipo ?? '').toUpperCase();
    const tipo = formatTipoPeriodoLabel(row.tipo) || 'Periodo';
    if (tipoNorm === 'MENSUAL' || tipoNorm === 'SEMESTRAL') {
      return `${tipo} ${row.numero}/${row.anio}`;
    }
    return `${tipo} ${row.anio}`;
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar el periodo <strong>{periodoLabel(params.row)}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar periodo', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.45 },
    {
      field: 'tipo',
      headerName: 'Tipo',
      flex: 0.75,
      renderCell: (p: any) => <TipoLabel tipo={p.row.tipo} />,
    },
    { field: 'numero', headerName: 'Número', flex: 0.6 },
    { field: 'anio', headerName: 'Año', flex: 0.55 },
    {
      field: 'fecha',
      headerName: 'Fecha ref.',
      flex: 0.85,
      renderCell: (p: any) => fmtDate(p.value),
    },
    {
      field: 'desde',
      headerName: 'Desde',
      flex: 0.85,
      renderCell: (p: any) => fmtDate(p.value),
    },
    {
      field: 'hasta',
      headerName: 'Hasta',
      flex: 0.85,
      renderCell: (p: any) => fmtDate(p.value),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      flex: 0.85,
      renderCell: (p: any) => <EstadoBadge estado={p.row.estado} />,
    },
    { field: '__actions', headerName: '', flex: 0.55, renderCell: renderActions },
  ];

  const filters = PERIODO_TABLE_FILTERS;

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Periodos', to: '/configuracion/calendario/periodos', icon: <FaRegCalendarCheck /> },
        { label: 'Tabla de Periodos', icon: <FaTable /> },
      ]} />

      <PageHeader
        icon={<FaTable />}
        iconColor="#0ea5e9"
        title="Tabla de Periodos"
        subtitle="Consulta y gestión de periodos del calendario"
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <PeriodoTipoResumenBar
        rows={tipoResumen}
        loading={resumenLoading}
        onTipoClick={handleTipoFilter}
        filterHint="tipo periodo"
      />

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        defaultSortField="anio"
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
